import { DropdownItem, DropdownItemProps, Icon, IconName, Link } from '@optimus-web/core';
import { closeAlert, lt, showInfoAlert } from '@mews/framework';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getBill } from '@mews-commander/legacy-entities/src/ducks';
import { FULL_BILL_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { Bill } from '@mews-commander/data-access';
import { useCurrentUser } from '@mews-commander/core';
import { refetchBill } from '../../../../ducks/bills';
import { fetchLinkedReservationOwners } from '../../../../utils';
import { useSmartGiftRedemptionUrlQuery } from './useSmartGiftRedemptionUrlQuery';

interface Props extends Omit<DropdownItemProps, 'label'> {
    billId: string;
    accountId: string;
}

const createSmartGiftUrl = (redemptionUrl: string, bill: Bill, languageCode: string) => {
    const searchParms = new URLSearchParams();

    searchParms.append('billId', bill.id);
    searchParms.append('languageCode', languageCode);

    return `${redemptionUrl}?${searchParms.toString()}`;
};

export const SmartGiftProcessPaymentOption = ({ billId, accountId, onClick, ...rest }: Props) => {
    const bill = useCommanderSelector((state) => getBill(state, { id: billId }));
    const user = useCurrentUser();
    const dispatch = useCommanderThunkDispatch();

    const { data } = useSmartGiftRedemptionUrlQuery();

    const smartGiftUrl =
        data?.redemptionUrl && bill ? createSmartGiftUrl(data.redemptionUrl, bill, user.languageCode) : null;
    const smartGiftEnabled = !bill?.isVirtual && !!smartGiftUrl;

    if (!smartGiftEnabled) return null;

    const refreshPage = (alertId: string) => {
        dispatch(
            refetchBill({
                billId,
                accountId,
                extent: FULL_BILL_EXTENT,
            })
        );
        dispatch(fetchLinkedReservationOwners({ billIds: [billId], accountId }));
        closeAlert(alertId);
    };

    const handleSmartGiftClick = (e: React.MouseEvent) => {
        onClick?.(e);
        const alertId = showInfoAlert({
            closeTimeout: 30 * 60 * 1000, // half an hour
            title: lt('SmartGiftAlertTitle'),
            description: lt('SmartGiftAlertDescription'),
            action: (
                <Link variant="basic" onClick={() => refreshPage(alertId)}>
                    {lt('RefreshBills')}
                </Link>
            ),
        });
        window.open(smartGiftUrl);
    };

    return (
        <DropdownItem
            {...rest}
            onClick={handleSmartGiftClick}
            leftDetail={<Icon size="small" value={IconName.ExternalLink} />}
            label={lt('AddSmartgiftVoucher')}
        />
    );
};
